import {
  AlarmClockCheck,
  Bell,
  BellRing,
  Calendar,
  CheckCircle2,
  Ghost,
  Hash,
  PencilLine,
  UserCheck,
  Users,
  XCircle,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { formatRelativeTime, getDayGroupLabel, type NotificationEvent } from '../../lib/notifications';

interface NotificationsFeedProps {
  events: NotificationEvent[];
  readIds: Set<string>;
  onSelect: (event: NotificationEvent) => void;
}

/**
 * Semantic color mapping for different event types.
 * We use these for the icons to give immediate context.
 */
const typeConfig: Record<NotificationEvent['type'], { icon: React.ElementType; colorClass: string }> = {
  NEW_RESERVATION_ONLINE: { icon: BellRing, colorClass: 'text-blue-400' },
  NEW_RESERVATION_MANUAL: { icon: Bell, colorClass: 'text-blue-400' },
  CANCELLATION: { icon: XCircle, colorClass: 'text-red-400' },
  MODIFICATION: { icon: PencilLine, colorClass: 'text-blue-400' },
  NO_SHOW_FLAGGED: { icon: Ghost, colorClass: 'text-slate-300' },
  CUSTOMER_ARRIVED: { icon: UserCheck, colorClass: 'text-amber-400' },
  TABLE_FREED: { icon: CheckCircle2, colorClass: 'text-green-400' },
};

// Human-readable source label.
function sourceLabel(source: NotificationEvent['source']): string {
  switch (source) {
    case 'online': return 'Online';
    case 'manual': return 'Χειροκίνητα';
    case 'phone': return 'Τηλέφωνο';
    case 'walk-in': return 'Walk-in';
    default: return source;
  }
}

const NotificationsFeed: React.FC<NotificationsFeedProps> = ({ events, readIds, onSelect }) => {
  if (!events.length) {
    return (
      <div className="rounded-2xl border border-border bg-card p-10 text-center">
        <AlarmClockCheck className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <p className="text-muted-foreground">
          Δεν υπάρχουν ειδοποιήσεις για τα φίλτρα που επέλεξες.
        </p>
      </div>
    );
  }

  // Group events by day label (Σήμερα / Χθες / dd/mm/yyyy).
  const groups = events.reduce<Record<string, NotificationEvent[]>>((acc, event) => {
    const group = getDayGroupLabel(event.timestamp);
    if (!acc[group]) acc[group] = [];
    acc[group].push(event);
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      {Object.entries(groups).map(([groupLabel, groupEvents]) => (
        <section key={groupLabel}>
          <h3 className="text-xs font-bold text-muted-foreground tracking-widest uppercase mb-2 px-1">
            {groupLabel}
          </h3>

          <div className="flex flex-col gap-2.5">
            {groupEvents.map((event) => {
              const isRead = readIds.has(event.id);
              const config = typeConfig[event.type] || { icon: Bell, colorClass: 'text-muted-foreground' };
              const Icon = config.icon;
              const isUnassignedTable = event.tableLabel === 'Χωρίς ανάθεση';

              const baseCardClasses = !isRead
                ? 'bg-card border-orange-500/40 shadow-[0_0_18px_rgba(249,115,22,0.20)]'
                : 'bg-card/60 border-border/60 hover:bg-accent/40';

              return (
                <button
                  key={event.id}
                  type="button"
                  onClick={() => onSelect(event)}
                  className={cn(
                    'w-full text-left px-4 py-3 rounded-2xl border transition-all active:scale-[0.98]',
                    baseCardClasses
                  )}
                >
                  {/* ── Top row: icon · title · relative time ── */}
                  <div className="flex items-start gap-3 mb-2">
                    <div
                      className={cn(
                        'p-2 rounded-xl flex items-center justify-center shrink-0',
                        !isRead
                          ? 'bg-zinc-800'
                          : 'bg-muted text-muted-foreground'
                      )}
                    >
                      <Icon className={cn("w-5 h-5", !isRead ? config.colorClass : "text-muted-foreground")} />
                    </div>

                    <div className="flex-1 min-w-0 flex items-start justify-between gap-1.5">
                      <div>
                        <h3
                          className={`font-semibold leading-snug ${
                            !isRead ? 'text-foreground' : 'text-foreground/80'
                          }`}
                        >
                          {event.title}
                        </h3>
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(event.timestamp)}
                        </span>
                      </div>

                    {/* Unread dot */}
                    {!isRead && (
                      <span className="mt-1 shrink-0 w-2 h-2 rounded-full bg-primary" />
                    )}
                    </div>
                  </div>

                  {/* ── Detail row: name · pax · time · table ── */}
                  <div className="pl-[3.25rem]">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground mb-2">
                      <span className="font-medium text-foreground">{event.customerName}</span>

                      <span className="text-border select-none">•</span>

                      <span className="flex items-center gap-1">
                        <Users size={12} className="text-muted-foreground/60 shrink-0" />
                        {event.partySize} άτομα
                      </span>

                      <span className="text-border select-none">•</span>

                      <span className="flex items-center gap-1">
                        <Calendar size={12} className="text-muted-foreground/60 shrink-0" />
                        {event.reservationTime?.slice(0, 5) || '—'}
                      </span>

                      <span className="text-border select-none">•</span>

                      <span
                        className={`flex items-center gap-1 ${
                          isUnassignedTable ? 'text-orange-400/80' : 'text-muted-foreground'
                        }`}
                      >
                        <Hash
                          size={12}
                          className={`shrink-0 ${
                            isUnassignedTable
                              ? 'text-orange-400/80'
                              : 'text-muted-foreground/60'
                          }`}
                        />
                        {event.tableLabel}
                      </span>
                    </div>

                    {/* Source badge */}
                    <div
                      className={cn(
                        'inline-flex items-center px-2.5 py-1 rounded-md border text-xs font-medium',
                        event.source === 'online'
                          ? 'bg-blue-500/10 border-blue-500/30 text-blue-300'
                          : 'bg-muted border-border/50 text-muted-foreground'
                      )}
                    >
                      {sourceLabel(event.source)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

export default NotificationsFeed;





